// src/cockpit4/components/ProductCapabilityPanel.tsx
// ============================================
// P2 产品差异化能力面板（三个子能力）
//   P2-1 闭环回流：DSO 成交 → 知识事实 → 写入 vault GEO 知识库
//   P2-3 合规风控：内容合规扫描 + 获客动作合规清单
//   P2-2 白标配置：品牌名/Logo/域名 白标设置（SaaS + 私有化白标）
// ============================================

import { useState } from "react";
import { RefreshCw, ShieldCheck, Palette, Save, Copy, Check, FileDown, Zap } from "lucide-react";

// ============ P2-1 闭环回流 ============

interface FeedbackRecord {
  id: string;
  customer: string;
  product: string;
  points: string[];
  keywords: string[];
  summary: string;
  time: string;
}

const FB_KEY = "xeaos_feedback_loop";
const loadFb = (): FeedbackRecord[] => {
  try {
    const raw = localStorage.getItem(FB_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return [];
};

function FeedbackLoop() {
  const [records, setRecords] = useState<FeedbackRecord[]>(loadFb);
  const [customer, setCustomer] = useState("");
  const [product, setProduct] = useState("");
  const [points, setPoints] = useState("");
  const [keywords, setKeywords] = useState("");
  const [summary, setSummary] = useState("");
  const [writing, setWriting] = useState<string | null>(null);
  const [msg, setMsg] = useState("");

  const saveAll = (list: FeedbackRecord[]) => {
    setRecords(list);
    try { localStorage.setItem(FB_KEY, JSON.stringify(list)); } catch { /* ignore */ }
  };

  const add = () => {
    if (!customer.trim() || !product.trim()) {
      setMsg("客户与成交产品必填");
      return;
    }
    const rec: FeedbackRecord = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      customer: customer.trim(),
      product: product.trim(),
      points: points.split("\n").map((s) => s.trim()).filter(Boolean),
      keywords: keywords.split(/[,，]/).map((s) => s.trim()).filter(Boolean),
      summary: summary.trim(),
      time: new Date().toISOString().replace("T", " ").slice(0, 19),
    };
    saveAll([rec, ...records]);
    setCustomer(""); setProduct(""); setPoints(""); setKeywords(""); setSummary("");
    setMsg("");
  };

  const buildMarkdown = (list: FeedbackRecord[]): string => {
    const lines: string[] = [];
    lines.push("# XEAOS 闭环回流 · GEO 知识库事实库");
    lines.push("");
    lines.push("> 来源：DSO 成交线索 → 自动沉淀为可被 AI 引用的品牌事实（P2-1 闭环回流）");
    lines.push("");
    for (const r of list) {
      lines.push(`## ${r.customer} · 成交 ${r.product}`);
      lines.push("");
      lines.push(`- 时间：${r.time}`);
      if (r.summary) lines.push(`- 一句话：${r.summary}`);
      lines.push(`- 关键词：${r.keywords.join("、") || "—"}`);
      lines.push(`- 核心卖点（证据链）：`);
      if (r.points.length) r.points.forEach((p) => lines.push(`  - ${p}`));
      lines.push("");
    }
    return lines.join("\n");
  };

  const writeVault = async (id: string) => {
    setWriting(id);
    setMsg("");
    try {
      const content = buildMarkdown(records);
      const resp = await fetch(`/vault-api/write?path=${encodeURIComponent("XEAOS/GEO知识库/闭环回流.md")}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, skipMeta: true }),
      });
      const data = await resp.json();
      setMsg(data?.success ? "已写入 vault：XEAOS/GEO知识库/闭环回流.md" : (data?.error || "写入失败"));
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
    setWriting(null);
  };

  const copyMd = async () => {
    try {
      await navigator.clipboard.writeText(buildMarkdown(records));
      setMsg("已复制 Markdown，可粘贴到 Obsidian");
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
        <RefreshCw size={11} className="text-emerald-400" />
        闭环回流：成交线索 → 知识事实 → 反哺 GEO 知识库（AI 可引用）
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="客户名 *"
          className="px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-emerald-500/40" />
        <input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="成交产品 *（如：酒店自助机 / XEAOS 专业版）"
          className="px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-emerald-500/40" />
      </div>
      <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="关键词（逗号分隔，如：酒店自助机,无人化,回本）"
        className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-emerald-500/40" />
      <textarea value={points} onChange={(e) => setPoints(e.target.value)} rows={3} placeholder="核心卖点 / 证据链（每行一条，AI 最爱的可核验事实）"
        className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-emerald-500/40 resize-y" />
      <input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="一句话总结（可选）"
        className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-emerald-500/40" />
      <div className="flex items-center gap-1.5">
        <button onClick={add} className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 transition">
          <Zap size={10} /> 记录成交线索
        </button>
        <button onClick={() => writeVault(records[0]?.id || "")} disabled={records.length === 0 || !!writing}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-mono text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/10 transition disabled:opacity-40">
          {writing ? "写入中…" : "写入 vault 知识库"}
        </button>
        <button onClick={copyMd} disabled={records.length === 0}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-mono text-gray-400 border border-white/[0.08] hover:text-white disabled:opacity-40">
          <Copy size={9} /> 复制 Markdown
        </button>
        {msg && <span className="text-[9px] font-mono text-emerald-400">{msg}</span>}
      </div>
      {records.length > 0 && (
        <div className="space-y-1 max-h-[220px] overflow-y-auto">
          {records.map((r) => (
            <div key={r.id} className="rounded border border-white/[0.05] bg-white/[0.02] p-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-white">{r.customer}</span>
                <span className="text-[9px] font-mono text-emerald-400">成交 {r.product}</span>
                <span className="ml-auto text-[8px] font-mono text-gray-600">{r.time}</span>
              </div>
              {r.summary && <p className="text-[9px] text-gray-400 mt-1">{r.summary}</p>}
              {r.points.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {r.points.map((p, i) => <p key={i} className="text-[9px] text-gray-500">· {p}</p>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ P2-3 合规风控 ============

const RISK_WORDS: Array<[string, string]> = [
  ["零风险", "绝对化承诺"], ["100%", "绝对化承诺"], ["必回本", "收益承诺"],
  ["稳赚", "收益承诺"], ["躺赚", "收益承诺"], ["包过", "承诺"], ["保证排名", "承诺"],
  ["无风险", "绝对化承诺"], ["绝对", "绝对化用词"], ["最", "绝对化用词（慎用）"],
  ["第一", "极限词（慎用）"], ["永久免费", "承诺"], ["马上暴富", "收益承诺"],
  ["自动私信", "违规获客动作"], ["爬取", "违规动作（需授权）"],
];

function ComplianceToolbox() {
  const [text, setText] = useState("");
  const [checked, setChecked] = useState(false);

  const hits = checked
    ? RISK_WORDS.filter(([w]) => text.includes(w)).map(([w, t]) => ({ word: w, type: t, count: text.split(w).length - 1 }))
    : [];

  const riskLevel = hits.length === 0 ? "ok" : hits.some((h) => h.count > 0 && /零风险|100%|必回本|稳赚|躺赚|保证|绝对/.test(h.word)) ? "high" : "mid";

  const runCheck = () => {
    setChecked(true);
  };

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
        <ShieldCheck size={11} className="text-amber-400" />
        合规风控：内容发布前扫描绝对化/收益承诺/违规动作用词
      </div>
      <textarea value={text} onChange={(e) => { setText(e.target.value); setChecked(false); }} rows={5}
        placeholder="把要发布的内容（公众号/知乎/官网文案）粘贴到这里，扫描合规风险…"
        className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-amber-500/40 resize-y" />
      <div className="flex items-center gap-2">
        <button onClick={runCheck} disabled={!text.trim()} className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-mono text-white bg-amber-500/25 hover:bg-amber-500/35 border border-amber-500/40 disabled:opacity-40">
          <ShieldCheck size={10} /> 开始扫描
        </button>
        {checked && (
          <span className={`text-[9px] font-mono ${riskLevel === "high" ? "text-red-400" : riskLevel === "mid" ? "text-amber-400" : "text-emerald-400"}`}>
            {riskLevel === "ok" ? "✓ 未发现明显风险词" : `发现 ${hits.length} 类风险词`}
          </span>
        )}
      </div>
      {hits.length > 0 && (
        <div className="space-y-1">
          {hits.map((h, i) => (
            <div key={i} className="flex items-center gap-2 rounded border border-red-500/20 bg-red-500/[0.05] px-2 py-1 text-[9px] font-mono">
              <span className="text-red-300">「{h.word}」×{h.count}</span>
              <span className="text-gray-500">{h.type} — 建议改为中性表述</span>
            </div>
          ))}
        </div>
      )}
      <div className="rounded border border-white/[0.06] bg-white/[0.02] p-2">
        <div className="text-[9px] font-mono text-gray-400 mb-1">获客动作合规红线（XEAOS 立场）</div>
        <ul className="text-[9px] text-gray-500 space-y-0.5">
          <li>· 禁止自动私信 / 群发骚扰（仅合规触达）</li>
          <li>· 禁止未授权爬取竞品 / 平台数据</li>
          <li>· 对外不点名竞品、不贬损；只和自己比</li>
          <li>· 收益表述不承诺具体数字与期限</li>
        </ul>
      </div>
    </div>
  );
}

// ============ P2-2 白标配置 ============

interface WhiteLabel {
  enabled: boolean;
  brandName: string;
  logoUrl: string;
  domain: string;
  agentMode: string;
}

const WL_KEY = "xeaos_whitelabel";
const DEFAULT_WL: WhiteLabel = {
  enabled: false,
  brandName: "XEAOS",
  logoUrl: "/logo.png",
  domain: "",
  agentMode: "代理商可二次分销，品牌/Logo/域名可替换",
};

function WhiteLabelConfig() {
  const [cfg, setCfg] = useState<WhiteLabel>(() => {
    try {
      const raw = localStorage.getItem(WL_KEY);
      if (raw) return { ...DEFAULT_WL, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT_WL;
  });
  const [saved, setSaved] = useState(false);

  const save = () => {
    try { localStorage.setItem(WL_KEY, JSON.stringify(cfg)); setSaved(true); setTimeout(() => setSaved(false), 1500); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-mono text-gray-400 flex items-center gap-1.5">
        <Palette size={11} className="text-purple-400" />
        白标配置：品牌名 / Logo / 域名（双版本部署：公有云 SaaS + 私有化白标贴牌）
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
          className={`px-2.5 py-1.5 rounded text-[10px] font-mono text-white transition ${cfg.enabled ? "bg-purple-600/80" : "bg-gray-600/60"}`}
        >
          {cfg.enabled ? "✓ 白标已启用" : "启用白标"}
        </button>
        <button onClick={save} className="flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-mono text-white bg-cyan-600/80 hover:bg-cyan-600 transition">
          <Save size={10} /> {saved ? "已保存 ✓" : "保存"}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="block text-[8px] font-mono text-gray-600 mb-0.5">白标品牌名</label>
          <input value={cfg.brandName} onChange={(e) => setCfg({ ...cfg, brandName: e.target.value })}
            className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-purple-500/40" />
        </div>
        <div>
          <label className="block text-[8px] font-mono text-gray-600 mb-0.5">Logo URL</label>
          <input value={cfg.logoUrl} onChange={(e) => setCfg({ ...cfg, logoUrl: e.target.value })}
            className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-purple-500/40" />
        </div>
        <div>
          <label className="block text-[8px] font-mono text-gray-600 mb-0.5">代理客户域名</label>
          <input value={cfg.domain} onChange={(e) => setCfg({ ...cfg, domain: e.target.value })}
            placeholder="如：agent.xshetech.com"
            className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-purple-500/40" />
        </div>
        <div>
          <label className="block text-[8px] font-mono text-gray-600 mb-0.5">代理模式说明</label>
          <input value={cfg.agentMode} onChange={(e) => setCfg({ ...cfg, agentMode: e.target.value })}
            className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 outline-none focus:border-purple-500/40" />
        </div>
      </div>
      <p className="text-[8px] font-mono text-gray-600 leading-relaxed">
        说明：本面板为白标品牌配置（SaaS 内展示层）；私有化独立部署脚本与代理分销台账属工程交付项，随 P2 迭代分批实施。
      </p>
    </div>
  );
}

// ============ 主面板 ============

export default function ProductCapabilityPanel() {
  const [sub, setSub] = useState<"loop" | "compliance" | "whitelabel">("loop");
  const items = [
    { id: "loop" as const, label: "闭环回流", icon: RefreshCw, color: "#10B981" },
    { id: "compliance" as const, label: "合规风控", icon: ShieldCheck, color: "#F59E0B" },
    { id: "whitelabel" as const, label: "白标配置", icon: Palette, color: "#A855F7" },
  ];
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button key={it.id} onClick={() => setSub(it.id)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-mono transition ${sub === it.id ? "text-white bg-white/[0.06] border border-white/[0.1]" : "text-gray-500 hover:text-gray-300 border border-transparent"}`}
              style={sub === it.id ? { color: it.color } : {}}>
              <Icon size={10} /> {it.label}
            </button>
          );
        })}
      </div>
      {sub === "loop" && <FeedbackLoop />}
      {sub === "compliance" && <ComplianceToolbox />}
      {sub === "whitelabel" && <WhiteLabelConfig />}
    </div>
  );
}
